/**
 * SVG 主题色替换工具。
 *
 * 职责：把 SVG 原始标记中约定的三种蓝色替换为对应的主题 CSS 变量，
 * 使内联进 DOM 的 SVG 能跟随 `--puppy-*` 动态换色，其余颜色保持原值。
 */
import { THEME_COLOR_CSS_VARIABLE_NAMES } from './theme-token';

/**
 * SVG 源色到主题 CSS 变量的映射。
 *
 * 约定（与 global.ts 中的主色配置对应）：
 * - #1C76EC 为主要蓝色，映射到 hex（--puppy-primary）；
 * - #003E8F 为较重蓝色，映射到 hexSuppl（--puppy-primary-suppl）；
 * - #D1E5FF 为较轻蓝色，映射到 hexHover（--puppy-primary-hover）。
 */
export const SVG_THEME_COLOR_VARIABLE_BY_SOURCE_HEX: Readonly<Record<string, string>> = {
    '#1C76EC': `var(${THEME_COLOR_CSS_VARIABLE_NAMES.primary})`,
    '#003E8F': `var(${THEME_COLOR_CSS_VARIABLE_NAMES.suppl})`,
    '#D1E5FF': `var(${THEME_COLOR_CSS_VARIABLE_NAMES.hover})`,
};

/**
 * 对 SVG 原始标记应用主题色替换。
 *
 * 替换规则：
 * 1. 大小写不敏感，避免遗漏 `#1c76ec` 这类写法；
 * 2. 只替换映射表中的三种蓝色，其他颜色原样保留；
 * 3. 输入必须是可信的静态 SVG 文本（由 `?raw` 导入），禁止传入用户内容。
 *
 * @param markup SVG 原始标记文本。
 * @returns 已将三种蓝色替换为 `var(--puppy-*)` 的 SVG 标记。
 */
export function applySvgThemeColorMapping(markup: string): string {
    let themedMarkup = markup;

    for (const [sourceHex, themeVariable] of Object.entries(SVG_THEME_COLOR_VARIABLE_BY_SOURCE_HEX)) {
        // hex 字符不包含正则元字符，直接构造即可；gi 保证大小写不敏感全局替换。
        const sourceColorPattern = new RegExp(sourceHex, 'gi');
        themedMarkup = themedMarkup.replace(sourceColorPattern, themeVariable);
    }

    return themedMarkup;
}
